import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../Common/Pagination";
import axiosInstance from "../../../utils/axiosInstance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API_ENDPOINTS } from "../../../constants/config";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Plus, Edit, Trash2, RefreshCw } from "lucide-react";

export function SectionList() {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [sectionData, setSectionData] = useState({
    name: "",
    selectedClasses: [],
  });
  const [error, setError] = useState(null);

  // Fetch classes and sections on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [classesResponse, sectionsResponse] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.classes),
          axiosInstance.get(API_ENDPOINTS.sections),
        ]);
        setClasses(classesResponse.data.data.classes);
        setSections(sectionsResponse.data.data.sections);
      } catch (error) {
        setError("Failed to load initial data");
      }
    };

    fetchInitialData();
  }, []);

  // Filtered and searched sections
  const filteredSections = sections.filter((section) =>
    section.name?.toLowerCase().includes(searchTerm?.toLowerCase() || "")
  );

  // Section Form Component
  function SectionForm({ classes, initialData = null, onSubmit, onCancel }) {
    const [formData, setFormData] = useState({
      name: initialData?.name || "",
      selectedClasses: initialData?.classes?.map((cls) => cls._id) || [],
    });

    const [error, setError] = useState(null);

    const handleClassToggle = (classId) => {
      setFormData((prev) => ({
        ...prev,
        selectedClasses: prev.selectedClasses.includes(classId)
          ? prev.selectedClasses.filter((id) => id !== classId)
          : [...prev.selectedClasses, classId],
      }));
    };

    const handleNameChange = (e) => {
      const value = e.target.value.toUpperCase();
      // Allow 1-3 capital letters A-Z
      if (value.length <= 3 && /^[A-Z]*$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          name: value,
        }));
      }
    };

    const handleSubmit = () => {
      if (!formData.name) {
        setError("Please enter a section name");
        return;
      }
      
      if (!/^[A-Z]{1,3}$/.test(formData.name)) {
        setError("Section name must be 1-3 capital letters (A-Z)");
        return;
      }

      if (formData.selectedClasses.length === 0) {
        setError("Please select at least one class");
        return;
      }

      setError(null);
      onSubmit(formData);
    };

    return (
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md">{error}</div>
        )}
        <div>
          <label className="block mb-2 text-sm font-medium">Section Name</label>
          <Input
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="Enter section name (e.g., A, AB, ABC)"
            maxLength={3}
            className="uppercase"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter 1-3 capital letters (A-Z)
          </p>
        </div>
        <div>
          <label className="block mb-2 text-sm font-medium">
            Select Classes
          </label>
          <div className="grid grid-cols-2 gap-2">
            {classes.map((cls, index) => (
              <div
                key={cls._id}
                className="flex items-center space-x-2"
                style={{
                  gridRow: (index % 6) + 1, // 5 items per column
                  gridColumn: Math.floor(index / 6) + 1, // Moves to next column after 5 items
                }}
              >
                <Checkbox
                  id={`class-${cls._id}`}
                  checked={formData.selectedClasses.includes(cls._id)}
                  onCheckedChange={() => handleClassToggle(cls._id)}
                />
                <label
                  htmlFor={`class-${cls._id}`}
                  className="text-sm font-medium leading-none"
                >
                  {cls.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {initialData ? "Update" : "Add"} Section
          </Button>
        </div>
      </div>
    );
  }

  const handleAddSection = async (sectionData) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.sections, {
        name: sectionData.name,
        classes: sectionData.selectedClasses,
      });

      // Update sections list with the new section
      setSections((prev) => [...prev, response.data.data]);

      // Close modal and reset form
      setIsAddModalOpen(false);
      setSectionData({ name: "", selectedClasses: [] });
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add section");
    }
  };

  // Delete Section Handler
  const handleDeleteSection = async (sectionId) => {
    try {
      await axiosInstance.delete(API_ENDPOINTS.section(sectionId));
      setSections(sections.filter((section) => section._id !== sectionId));
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete section");
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <CardTitle>Section Management</CardTitle>
          <div className="flex flex-col sm:flex-row w-full md:w-auto space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative w-full sm:w-64">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={20}
              />
              <Input
                placeholder="Search sections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 w-full"
              />
            </div>
            <Button
              onClick={() => {
                setSectionData({ name: "", selectedClasses: [] });
                setIsAddModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Section Name</TableHead>
                <TableHead>Classes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSections.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((section) => (
                <TableRow key={section._id}>
                  <TableCell>{section.name}</TableCell>
                  <TableCell>
                    {section.classes?.map((cls) => cls.name).join(", ") ||
                      "No classes"}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigate(`/dashboard/settings/sections/edit/${section._id}`, {
                          state: { section },
                        });
                      }}
                    >
                      <Edit className="mr-2" size={16} /> Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteSection(section._id)}
                    >
                      <Trash2 className="mr-2" size={16} /> Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination
          currentPage={page}
          totalItems={filteredSections.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setPage}
          onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
          itemLabel="sections"
        />
      </CardContent>

      {/* Add Section Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Section</DialogTitle>
            <DialogDescription>
              Create a new section and assign it to multiple classes
            </DialogDescription>
          </DialogHeader>
          <SectionForm
            classes={classes}
            onSubmit={handleAddSection}
            onCancel={() => setIsAddModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

    </Card>
  );
}

export default SectionList;
